import { type SubmitEvent, useMemo, useState } from "react";
import accessStyles from "./AccessCodeEntry.module.css";
import gateStyles from "./Gate.module.css"

interface Props {
  onSubmit: (code: string) => void | Promise<void>;
  error?: string;
  isSubmitting?: boolean;
  title?: string;
  subtitle?: string;
  dataCy?: string;
}

export function AccessCodeEntry({
  onSubmit,
  error,
  isSubmitting = false,
  title = "Private Homepage",
  subtitle = "The site you are trying to visit is private. Please provide an access code to view this site.",
  dataCy,
}: Props) {
  const [code, setCode] = useState("");

  const canSubmit = useMemo(
    () => code.trim().length > 0 && !isSubmitting,
    [code, isSubmitting],
  );

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    void onSubmit(code);
  };

  return (
    <div className={gateStyles.entry}>
      <div className={gateStyles.panel} data-cy={dataCy}>
        <h1 className={gateStyles.title}>{title}</h1>
        <p className={gateStyles.subtitle}>{subtitle}</p>

        <form onSubmit={handleSubmit}>
          <label htmlFor="access-code" className={accessStyles.formLabel}>
            Access code
          </label>

          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            autoFocus
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="e.g. ABC123"
            className={accessStyles.formInput}
            id="access-code"
            data-cy="access-code-input"
          />

          {error ? <div className={accessStyles.formError}>{error}</div> : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className={accessStyles.formSubmit}
            data-cy="access-code-submit"
          >
            {isSubmitting ? "Checking…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
